"use client";
import { useState, useRef, useEffect } from "react";
import { motion, useMotionValue, animate } from "motion/react";
import { cn } from "../../lib/utils";

export const LayoutGrid = ({ cards }) => {
  // --- State for mobile carousel ---
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef(null);
  const x = useMotionValue(0);

  // --- Logic for mobile carousel animation ---
  const handleDragEnd = (event, info) => {
    const offset = info.offset.x;
    const velocity = info.velocity.x;
    const cardWidth = containerRef.current?.offsetWidth || 300;

    if (Math.abs(velocity) >= 500 || Math.abs(offset) > cardWidth / 4) {
      if (offset > 0 && currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      } else if (offset < 0 && currentIndex < cards.length - 1) {
        setCurrentIndex(currentIndex + 1);
      }
    }
  };

  useEffect(() => {
    const cardWidth = containerRef.current?.offsetWidth || 300;
    animate(x, -currentIndex * cardWidth, {
      type: "spring",
      stiffness: 300,
      damping: 30,
    });
  }, [currentIndex, x]);

  return (
    <div className="w-full h-full p-10">
      {/* --- Desktop Grid --- */}
      <div className="hidden md:grid grid-cols-1 md:grid-cols-3 max-w-7xl mx-auto gap-4">
        {cards.map((card, i) => (
          <div
            key={i}
            className={cn(card.className, "min-h-[400px] md:min-h-[300px]")}
          >
            {/* 1. Removed onClick handler and conditional classes. Removed bg-white. */}
            <motion.div
              className={cn(
                card.className,
                "relative overflow-hidden rounded-xl h-full w-full"
              )}
            >
              <ImageComponent card={card} />
            </motion.div>
          </div>
        ))}
      </div>

      {/* --- Mobile Swipeable Carousel --- */}
      <div className="md:hidden w-full overflow-hidden" ref={containerRef}>
        <motion.div
          className="flex cursor-grab active:cursor-grabbing"
          drag="x"
          dragElastic={0.2}
          dragMomentum={false}
          onDragEnd={handleDragEnd}
          style={{ x }}
        >
          {cards.map((card, i) => (
            <div key={i} className="flex-shrink-0 w-full px-2">
              {/* 2. Removed onClick and bg-white from mobile view as well */}
              <motion.div
                className={cn(
                  "relative overflow-hidden min-h-[400px] rounded-xl"
                )}
              >
                <ImageComponent card={card} />
              </motion.div>
            </div>
          ))}
        </motion.div>

        {/* --- Pagination Dots --- */}
        <div className="flex justify-center gap-2 mt-6">
          {cards.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentIndex(i)}
              className={cn(
                "h-2 rounded-full transition-all duration-300",
                currentIndex === i ? "bg-blue-600 w-8" : "bg-gray-300 w-2"
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// This component remains the same
const ImageComponent = ({ card }) => {
  return (
    <motion.img
      src={card.thumbnail}
      height="500"
      width="500"
      className={cn(
        "object-cover absolute inset-0 h-full w-full transition duration-200",
        card.objectPosition || "object-center"
      )}
      alt="thumbnail"
    />
  );
};
